import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { ApiError } from '../../api/client.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  listStates,
  listDistricts,
  listTehsilsByDistrict,
  listGeoScopes,
  getGeoPolicy,
  checkGeoScope,
  assignGeoScope,
  revokeGeoScope,
  type StateRow,
  type DistrictRow,
  type TehsilRow,
  type GeoScopeRow,
  type GeoScopeConflict,
  type GeoScopePolicy,
  type GeoScopeType,
} from '../../api/orgApi.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface Props {
  readonly userId: string;
}

const SCOPE_LABEL: Record<GeoScopeType, string> = {
  ALL_INDIA: 'All India',
  STATE: 'Whole state (all districts)',
  DISTRICT: 'District',
  SUBDISTRICT: 'Sub-district (tehsil)',
};

function placeOf(s: GeoScopeRow): string {
  if (s.scopeType === 'ALL_INDIA') return 'All India';
  if (s.scopeType === 'STATE') return `${s.stateName ?? '—'} (all districts)`;
  if (s.scopeType === 'DISTRICT') return s.districtName ?? '—';
  return s.tehsilName ?? '—';
}

/**
 * Geography on the user's profile. Two officers of the same group and level
 * may not cover the same ground at the same time; the check runs here so the
 * clash is shown before saving, and the API refuses it regardless.
 */
export function UserGeographyPanel({ userId }: Props): ReactNode {
  const { token } = useAuth();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const [scopes, setScopes] = useState<readonly GeoScopeRow[] | null>(null);
  const [policy, setPolicy] = useState<GeoScopePolicy | null>(null);
  const [states, setStates] = useState<readonly StateRow[]>([]);
  const [districts, setDistricts] = useState<readonly DistrictRow[]>([]);
  const [tehsils, setTehsils] = useState<readonly TehsilRow[]>([]);

  const [scopeType, setScopeType] = useState<GeoScopeType>('DISTRICT');
  const [stateId, setStateId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [tehsilId, setTehsilId] = useState('');
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState('');

  const [conflicts, setConflicts] = useState<readonly GeoScopeConflict[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    Promise.all([listGeoScopes(token, 'USER', userId), getGeoPolicy(token, 'USER', userId)])
      .then(([s, p]) => { setScopes(s); setPolicy(p); })
      .catch((e) => setError((e as Error).message));
  }, [token, userId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    listStates(token).then((r) => setStates(r.data))
      .catch((e) => { setStates([]); setError((e as Error).message); });
  }, [token]);

  // Districts follow the chosen state; a state with no pick shows every district.
  useEffect(() => {
    listDistricts(token, stateId || undefined)
      .then((r) => setDistricts(r.data))
      .catch((e) => { setDistricts([]); setError((e as Error).message); });
  }, [token, stateId]);

  useEffect(() => {
    if (!districtId) { setTehsils([]); return; }
    listTehsilsByDistrict(token, districtId).then(setTehsils)
      .catch((e) => { setTehsils([]); setError((e as Error).message); });
  }, [token, districtId]);

  function body() {
    return {
      ownerType: 'USER' as const,
      ownerId: userId,
      scopeType,
      stateId: scopeType === 'STATE' ? stateId : null,
      districtId: scopeType === 'DISTRICT' ? districtId : null,
      tehsilId: scopeType === 'SUBDISTRICT' ? tehsilId : null,
      effectiveFromDate: fromDate,
      effectiveToDate: toDate || null,
    };
  }

  async function onCheck(): Promise<void> {
    setError(null); setNotice(null); setBusy(true);
    try {
      const res = await checkGeoScope(token, body());
      setConflicts(res.conflicts);
      if (res.conflicts.length === 0) setNotice('No clash — this area is free for that period.');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally { setBusy(false); }
  }

  async function onAssign(): Promise<void> {
    setError(null); setNotice(null); setBusy(true);
    try {
      await assignGeoScope(token, body());
      setConflicts([]);
      setNotice('Geography assigned.');
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally { setBusy(false); }
  }

  async function onRevoke(scopeId: string): Promise<void> {
    setBusy(true);
    try { await revokeGeoScope(token, scopeId); reload(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  const exclusive = policy?.geographyPolicy === 'EXCLUSIVE';

  return (
    <section aria-label="user-geography">
      <h2>Geography</h2>

      {policy ? (
        <p aria-label="geo-policy">
          {policy.roleCode} — {policy.functionalGroup}/{policy.hierarchyLevel} —{' '}
          {exclusive
            ? 'exclusive: no other holder of this role may cover the same area at the same time'
            : `policy: ${policy.geographyPolicy.toLowerCase()}`}
        </p>
      ) : (
        <p aria-label="geo-policy">Assign a role first — the geography rule follows the role.</p>
      )}

      {error ? <div role="alert">{error}</div> : null}
      {notice ? <div aria-label="geo-notice">{notice}</div> : null}

      {conflicts.length > 0 ? (
        <div role="alert" aria-label="geo-conflicts">
          <strong>Already covered:</strong>
          <ul>
            {conflicts.map((c) => (
              <li key={`${c.ownerId}-${c.placeName}-${day(c.effectiveFromDate)}`}>
                {c.displayName} ({c.roleCode}) holds {c.placeName} from {day(c.effectiveFromDate)}
                {c.effectiveToDate ? ` to ${day(c.effectiveToDate)}` : ' onwards'}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <table aria-label="geo-scope-list">
        <caption>Assigned areas</caption>
        <thead>
          <tr><th>Level</th><th>Area</th><th>From</th><th>To</th><th>Action</th></tr>
        </thead>
        <tbody>
          {(scopes ?? []).map((s) => (
            <tr key={s.scopeId} data-testid={`geo-${s.scopeId}`}>
              <td>{SCOPE_LABEL[s.scopeType]}</td>
              <td>{placeOf(s)}</td>
              <td>{day(s.effectiveFromDate)}</td>
              <td>{day(s.effectiveToDate)}</td>
              <td>
                <button type="button" aria-label={`revoke-${s.scopeId}`} disabled={busy}
                        onClick={() => void onRevoke(s.scopeId)}>Remove</button>
              </td>
            </tr>
          ))}
          {scopes && scopes.length === 0 ? (
            <tr><td colSpan={5}>No geography assigned yet.</td></tr>
          ) : null}
        </tbody>
      </table>

      <fieldset>
        <legend>Add area</legend>

        <label htmlFor="geo-scope-type">Level
          <select id="geo-scope-type" aria-label="geo-scope-type" value={scopeType}
                  onChange={(e) => setScopeType(e.target.value as GeoScopeType)}>
            {(Object.keys(SCOPE_LABEL) as GeoScopeType[]).map((k) => (
              <option key={k} value={k}>{SCOPE_LABEL[k]}</option>
            ))}
          </select>
        </label>

        {scopeType === 'STATE' ? (
          <label htmlFor="geo-state">State
            <select id="geo-state" aria-label="geo-state" value={stateId}
                    onChange={(e) => setStateId(e.target.value)}>
              <option value="">Select…</option>
              {states.map((s) => <option key={s.stateId} value={s.stateId}>{s.stateName}</option>)}
            </select>
          </label>
        ) : null}

        {scopeType === 'DISTRICT' || scopeType === 'SUBDISTRICT' ? (
          <>
            <label htmlFor="geo-filter-state">State (filter)
              <select id="geo-filter-state" aria-label="geo-filter-state" value={stateId}
                      onChange={(e) => { setStateId(e.target.value); setDistrictId(''); setTehsilId(''); }}>
                <option value="">All states</option>
                {states.map((s) => <option key={s.stateId} value={s.stateId}>{s.stateName}</option>)}
              </select>
            </label>
            <label htmlFor="geo-district">District
              <select id="geo-district" aria-label="geo-district" value={districtId}
                      onChange={(e) => { setDistrictId(e.target.value); setTehsilId(''); }}>
                <option value="">Select…</option>
                {districts.map((d) => (
                  <option key={d.districtId} value={d.districtId}>{d.districtName}</option>
                ))}
              </select>
            </label>
          </>
        ) : null}

        {scopeType === 'SUBDISTRICT' ? (
          <label htmlFor="geo-tehsil">Sub-district
            <select id="geo-tehsil" aria-label="geo-tehsil" value={tehsilId}
                    onChange={(e) => setTehsilId(e.target.value)}>
              <option value="">Select…</option>
              {tehsils.map((t) => <option key={t.tehsilId} value={t.tehsilId}>{t.tehsilName}</option>)}
            </select>
          </label>
        ) : null}

        <label htmlFor="geo-from">Effective from
          <DateInput id="geo-from" aria-label="geo-from" type="date" value={fromDate}
                 onValueChange={(nextValue) => setFromDate(nextValue)} />
        </label>
        <label htmlFor="geo-to">Effective to (blank = open ended)
          <DateInput id="geo-to" aria-label="geo-to" type="date" value={toDate}
                 onValueChange={(nextValue) => setToDate(nextValue)} />
        </label>

        <button type="button" aria-label="geo-check" disabled={busy} onClick={() => void onCheck()}>
          Check availability
        </button>
        <button type="button" aria-label="geo-assign" disabled={busy} onClick={() => void onAssign()}>
          Assign
        </button>
      </fieldset>
    </section>
  );
}
